<script setup lang="ts">
import { useMyPageStore } from '@/store/myPageStore';
import { isEmail, isMaxLength, isPassword, isPhone, isRequired, passwordMatch } from '@/utils';
import { useSignStore } from '@/modules/sign/store';

const myPageStore = useMyPageStore();
const { myInfo, positionList } = storeToRefs(myPageStore);
const formRef = ref();
const changePasswordFormRef = ref();
const isValid = ref(false);
const changePasswordIsValid = ref(false);

const changePasswordModal = ref(false);
const changePasswordInfo = ref({
  oldPasswd: '',
  oldPasswdVisible: false,
  passwd: '',
  passwddVisible: false,
  confirmPassword: '',
  isConfirmPasswordVisible: false,
});
const isConfirmPasswordBlurred = ref(false);

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:modelValue']);
const close = () => {
  confirmAlert('취소', '입력한 정보가 모두 초기화됩니다.\n정말로 취소하시겠습니까?', () => {
    emit('update:modelValue', false);
  });
};
const openChangePassword = () => {
  changePasswordInfo.value = {
    oldPasswd: '',
    oldPasswdVisible: false,
    passwd: '',
    passwddVisible: false,
    confirmPassword: '',
    isConfirmPasswordVisible: false,
  };
  isConfirmPasswordBlurred.value = false;
  changePasswordModal.value = true;
};

const confirmPasswordRules = computed(() => {
  const rules = [isRequired('새 비밀번호 확인'), isPassword()];
  if (isConfirmPasswordBlurred.value) {
    rules.push(passwordMatch(changePasswordInfo.value.passwd));
  }
  return rules;
});

const changePassword = async () => {
  if (!(await changePasswordFormRef.value.validate()).valid) {
    return;
  } else {
    const params = {
      oldPasswd: changePasswordInfo.value.oldPasswd,
      passwd: changePasswordInfo.value.passwd,
    };
    await myPageStore.changePassword(params);
    await successAlert('성공', '비밀번호가 변경되었습니다.\n다시 로그인해주세요.');
    useSignStore().logout();
  }
};

const setMyInfo = async () => {
  if (!(await formRef.value.validate()).valid) {
    return;
  } else {
    await myPageStore
      .setMyInfo({
        cellPhone: myInfo.value.cellPhone!,
        email: myInfo.value.email!,
      })
      .then(async () => {
        await successAlert('성공', '정보가 저장되었습니다.');
        emit('update:modelValue', false);
      });
  }
};

onMounted(() => {
  myPageStore.updateMyInfo(false);
});
</script>

<template>
  <BaseModal
    :modelValue="props.modelValue"
    @update:modelValue="(value: boolean) => emit('update:modelValue', value)"
    title="내 정보"
    width="550px"
    height="380px"
  >
    <v-form ref="formRef" v-model="isValid">
      <v-card class="mb-6" elevation="0">
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12" md="12">
              <div class="d-flex justify-space-between ga-2">
                <div class="form-field">
                  <label class="field-label">소속회사</label>
                  <div>{{ myInfo.companyName || '-' }}</div>
                </div>
                <div class="form-field">
                  <label class="field-label">소속조직</label>
                  <div>{{ myInfo.organizationName || '-' }}</div>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div class="d-flex justify-space-between ga-2">
                <div class="form-field">
                  <label class="field-label">이름</label>
                  <div>{{ myInfo.name }}</div>
                </div>
                <div class="form-field">
                  <label class="field-label">직책/직위</label>
                  <div v-if="positionList[myInfo.positionCode]">
                    {{ positionList[myInfo.positionCode].name }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div class="d-flex justify-space-between ga-2">
                <div class="form-field">
                  <label class="field-label">사원번호</label>
                  <div>{{ myInfo.employeeNumber }}</div>
                </div>
                <div class="form-field">
                  <label class="field-label">입사일자</label>
                  <div>{{ myInfo.joinDate }}</div>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div class="d-flex justify-space-between ga-2">
                <div class="form-field w-49">
                  <label class="field-label">연락처</label>
                  <v-text-field
                    v-model="myInfo.cellPhone"
                    :rules="[isRequired('연락처'), isPhone('연락처'), isMaxLength('연락처', 15)]"
                    maxlength="15"
                  />
                </div>
                <div class="form-field w-49">
                  <label class="field-label">이메일</label>
                  <v-text-field
                    v-model="myInfo.email"
                    :rules="[isRequired('이메일'), isEmail(), isMaxLength('이메일', 30)]"
                    maxlength="30"
                  />
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div class="form-field">
                <v-btn variant="outlined" color="primary" @click="openChangePassword"
                  >비밀번호 변경</v-btn
                >
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
    <template #actions>
      <v-btn variant="outlined" color="primary" @click="close">취소</v-btn>
      <v-btn variant="elevated" color="primary" @click="setMyInfo">저장</v-btn>
    </template>
  </BaseModal>
  <BaseModal
    :modelValue="changePasswordModal"
    @update:modelValue="changePasswordModal = false"
    title="비밀번호 변경"
    width="550px"
    height="380px"
  >
    <v-form ref="changePasswordFormRef" v-model="changePasswordIsValid">
      <v-card class="mb-6" elevation="0">
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12" md="12">
              <div class="form-field">
                <label class="field-label">현재 비밀번호</label>
                <v-text-field
                  v-model="changePasswordInfo.oldPasswd"
                  :clearable="true"
                  :rules="[isRequired('현재 비밀번호')]"
                  maxlength="20"
                  :append-inner-icon="
                    changePasswordInfo.oldPasswdVisible ? 'custom:eyeOn' : 'custom:eyeOff'
                  "
                  @click:append-inner="
                    changePasswordInfo.oldPasswdVisible = !changePasswordInfo.oldPasswdVisible
                  "
                  :type="changePasswordInfo.oldPasswdVisible ? 'text' : 'password'"
                />
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div class="form-field">
                <label class="field-label">새 비밀번호</label>
                <v-text-field
                  v-model="changePasswordInfo.passwd"
                  :clearable="true"
                  :rules="[isRequired('새 비밀번호'), isPassword()]"
                  maxlength="20"
                  :append-inner-icon="
                    changePasswordInfo.passwddVisible ? 'custom:eyeOn' : 'custom:eyeOff'
                  "
                  @click:append-inner="
                    changePasswordInfo.passwddVisible = !changePasswordInfo.passwddVisible
                  "
                  :type="changePasswordInfo.passwddVisible ? 'text' : 'password'"
                />
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div class="form-field">
                <label class="field-label">새 비밀번호 확인</label>
                <v-text-field
                  v-model="changePasswordInfo.confirmPassword"
                  :clearable="true"
                  :rules="confirmPasswordRules"
                  maxlength="20"
                  @blur="isConfirmPasswordBlurred = true"
                  :append-inner-icon="
                    changePasswordInfo.isConfirmPasswordVisible ? 'custom:eyeOn' : 'custom:eyeOff'
                  "
                  @click:append-inner="
                    changePasswordInfo.isConfirmPasswordVisible =
                      !changePasswordInfo.isConfirmPasswordVisible
                  "
                  :type="changePasswordInfo.isConfirmPasswordVisible ? 'text' : 'password'"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
    <template #actions>
      <v-btn variant="outlined" color="body-light" @click="changePasswordModal = false">취소</v-btn>
      <v-btn variant="elevated" color="primary" @click="changePassword">저장</v-btn>
    </template>
  </BaseModal>
</template>
<style scoped lang="scss"></style>
